import { css } from "@emotion/css";
import { ErrorPanel } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { isPodResource } from "@octopusdeploy/octopus-server-client";
import React, { useMemo } from "react";
import { getLiveResourceStatusIcon } from "~/areas/projects/components/Observability/LiveStatusIcons";
import type { LiveStatusObjectDrawerLoadingTabProps } from "~/areas/projects/components/Observability/ObjectDrawer/Tabs/TabProps";
import { createErrorInfo } from "~/areas/tasks/components/Task/Kubernetes/createErrorInfo";
import { Skeleton } from "~/components/Skeleton/Skeleton";
import DateFormatter from "~/utils/DateFormatter/index";
type SummaryTabProps = LiveStatusObjectDrawerLoadingTabProps;
const styles = {
    summaryDetailsList: css({
        display: "grid",
        marginBlock: 0,
        gridTemplateColumns: "max-content auto",
        "> dt": {
            gridColumn: 1,
            font: text.body.regular.medium,
            color: themeTokens.color.text.secondary,
            padding: space[16],
            paddingLeft: 0,
            minWidth: `calc(11 * ${space[16]})`, // 176px
        },
        "> dd": {
            gridColumn: 2,
            font: text.body.regular.medium,
            color: themeTokens.color.text.primary,
            padding: space[16],
            paddingRight: 0,
            marginInlineStart: 0, //resets this from the browser default
        },
    }),
    statusDD: css({
        display: "flex",
        flexDirection: "row",
        gap: space[8],
    }),
    summaryDetailList: css({
        margin: 0,
        listStyleType: "disc",
        listStylePosition: "inside",
    }),
    loadingDiv: css({
        height: "20px",
    }),
};
export const SummaryTab = (props: SummaryTabProps) => {
    const { detailedResource, isLoading, error } = props;
    const formatArrayDetails = (label: string, values: string[] | undefined) => values &&
        values.length > 0 && (<>
                <dt>{label}</dt>
                <dd>
                    <ul className={styles.summaryDetailList}>
                        {values.map((v) => (<li key={v}>{v}</li>))}
                    </ul>
                </dd>
            </>);
    const formatRecordDetails = (label: string, values: Record<string, string>, delimiter: string) => {
        const entries = Object.entries(values);
        return (entries.length > 0 && (<>
                    <dt>{label}</dt>
                    <dd>
                        <ul className={styles.summaryDetailList}>
                            {entries.map(([k, v]) => (<li key={k}>
                                    {k}
                                    {delimiter}
                                    {v}
                                </li>))}
                        </ul>
                    </dd>
                </>));
    };
    const customDetails = useMemo(() => {
        if (isPodResource(detailedResource)) {
            return formatArrayDetails("Containers", detailedResource.Details?.Containers);
        }
    }, [detailedResource]);
    const detailedResourceErrorInfo = createErrorInfo(error);
    return (<div>
            {detailedResourceErrorInfo && <ErrorPanel error={detailedResourceErrorInfo}/>}
            {!detailedResourceErrorInfo && (<dl className={styles.summaryDetailsList}>
                    {isLoading &&
                Array.from({ length: 5 }, (_, i) => (<>
                                <dt>
                                    <div className={styles.loadingDiv}>
                                        <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                    </div>
                                </dt>
                                <dd>
                                    <div className={styles.loadingDiv}>
                                        <Skeleton shape={"Rounded"} borderRadius={"small"}/>
                                    </div>
                                </dd>
                            </>))}
                    {!isLoading && detailedResource && (<>
                            <dt>State</dt>
                            <dd className={styles.statusDD}>
                                {getLiveResourceStatusIcon(detailedResource.Status)} {detailedResource.Status}
                            </dd>
                            {customDetails}
                            {detailedResource.Details && (<>
                                    {formatRecordDetails("Labels", detailedResource.Details.Labels, "=")}
                                    {formatRecordDetails("Annotations", detailedResource.Details.Annotations, ": ")}
                                    <dt>Creation Timestamp</dt>
                                    <dd>
                                        <span title={DateFormatter.dateToLongFormat(detailedResource.Details.CreationTimestamp) || undefined}>{DateFormatter.dateToShortFormat(detailedResource.Details.CreationTimestamp)}</span>
                                    </dd>
                                </>)}
                        </>)}
                </dl>)}
        </div>);
};
